import { actions } from "@mixitone/actions";
import { Button, ButtonGroup, Spinner } from "@mixitone/components";
import { ClubDocument } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { mixitoneImagesPublicUrl } from "@mixitone/util";
import { useCallback, useEffect, useRef, useState } from "react";
import { pdfjs, Document, Page } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function availableHeight(container: HTMLDivElement, controls: HTMLDivElement) {
  const { clientHeight } = container;
  const controlsHeight = controls.clientHeight;
  return clientHeight - (controlsHeight + 16);
}

function availableWidth(container: HTMLDivElement) {
  return container.clientWidth;
}

const PdfView = ApplicationView(
  ({
    document,
    required,
    onClose,
  }: {
    document: ClubDocument;
    required?: boolean;
    onClose: (accepted: boolean) => void;
  }) => {
    const [loading, setLoading] = useState(true);
    const [numPages, setNumPages] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [url, setUrl] = useState<string | null>(null);
    const [urlError, setUrlError] = useState<string | null>(null);
    const dialogRef = useRef<HTMLDialogElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const controlsRef = useRef<HTMLDivElement>(null);
    const [pageWidth, setPageWidth] = useState<number | undefined>(undefined);

    useEffect(() => {
      if (!dialogRef.current) return;

      const dialog = dialogRef.current;
      dialog.showModal();

      const handleClose = () => {
        onClose(false);
      };

      dialog.addEventListener("close", handleClose);

      return () => {
        dialog.removeEventListener("close", handleClose);
        if (dialog) {
          dialog.close();
        }
      };
    }, []);

    useEffect(() => {
      let cancelled = false;
      setLoading(true);
      setUrlError(null);

      async function loadUrl() {
        if (document.file_storage === "private_documents") {
          if (!document.id) return;
          try {
            const signed = await actions.documents.signDocumentRead({ clubDocumentId: document.id });
            if (!cancelled) setUrl(signed.url);
          } catch (error) {
            console.error("Error opening document:", error);
            if (!cancelled) {
              setUrl(null);
              setUrlError("Could not open this document.");
            }
          }
          return;
        }

        if (document.account_id && document.document_id) {
          setUrl(mixitoneImagesPublicUrl(`${document.account_id}/documents/${document.document_id}`));
        }
      }

      loadUrl();
      return () => {
        cancelled = true;
      };
    }, [document.id, document.file_storage, document.account_id, document.document_id]);

    const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      setPageNumber(1);
      setLoading(false);
    };
    const handleClickPrevious = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (pageNumber > 1) {
        setPageNumber(pageNumber - 1);
      }
    };
    const handleClickNext = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (numPages && pageNumber < numPages) {
        setPageNumber(pageNumber + 1);
      }
    };

    const scaleToFit = useCallback(() => {
      // When the width is set then the height does not get applied to the canvas
      // So we need to do a scale transform to fit the available height
      if (!containerRef.current) return;
      if (!controlsRef.current) return;
      const canvas = containerRef.current.querySelector("canvas");
      if (!canvas) return;
      if (!canvas.style.width) return;
      if (!canvas.style.height) return;

      const height = availableHeight(containerRef.current, controlsRef.current);
      const width = availableWidth(containerRef.current);

      const { clientHeight, clientWidth } = canvas;

      const heightScale = height / clientHeight;
      const widthScale = width / clientWidth;

      const scale = Math.min(heightScale, widthScale);
      const scaledWidth = Math.round(clientWidth * scale);

      if (scaledWidth !== pageWidth) {
        setPageWidth(scaledWidth);
      }
    }, [pageWidth]);

    const handlePageLoadSuccess = useCallback(() => {
      if (!containerRef.current) return;
      if (!controlsRef.current) return;
      if (!dialogRef.current) return;
      if (pageWidth) return;

      scaleToFit();
    }, [scaleToFit]);

    useEffect(() => {
      const handle = setInterval(() => {
        scaleToFit();
      }, 1000);

      return () => clearInterval(handle);
    }, [scaleToFit]);

    return (
      <dialog
        ref={dialogRef}
        className="pdf-viewer rounded-lg shadow-xl backdrop:bg-black/50 bg-transparent h-[100vh] w-[100vw] min-w-[100vw] max-w-[100vw] min-h-[100vh] max-h-[100vh] flex justify-center items-center"
      >
        <div
          ref={containerRef}
          className="rounded bg-white flex flex-col items-center justify-between overflow-auto w-full h-full"
        >
          {urlError && <p className="p-4 text-sm">{urlError}</p>}
          {url && (
            <>
              <Document file={url} onLoadSuccess={handleDocumentLoadSuccess} loading={<Spinner size={64} />}>
                <Page
                  pageNumber={pageNumber}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  onLoadSuccess={handlePageLoadSuccess}
                  width={pageWidth}
                />
              </Document>
              <div className="flex items-center justify-between p-4 gap-8" ref={controlsRef}>
                {numPages && numPages > 1 ? (
                  <div className="flex items-center justify-center gap-4">
                    <ButtonGroup>
                      <Button kind="secondary" onClick={handleClickPrevious} disabled={pageNumber <= 1}>
                        Previous
                      </Button>
                      <Button kind="secondary" onClick={handleClickNext} disabled={pageNumber >= numPages}>
                        Next
                      </Button>
                    </ButtonGroup>
                  </div>
                ) : (
                  <div />
                )}
                <span className="text-sm">
                  {pageNumber} of {numPages}
                </span>
                <div className="flex items-center gap-2">
                  {required ? (
                    <ButtonGroup>
                      <Button kind="secondary" onClick={() => onClose(false)}>
                        Reject
                      </Button>
                      <Button kind="primary" onClick={() => onClose(true)}>
                        Accept
                      </Button>
                    </ButtonGroup>
                  ) : (
                    <Button kind="secondary" onClick={() => onClose(false)}>
                      Done
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </dialog>
    );
  },
);

export { PdfView };
